import {
  VentureSectorCreateDocument,
  VentureSectorDeleteDocument,
  VentureSectorGetByIdDocument,
  VentureSectorListDocument,
  VentureSectorUpdateDocument,
  VentureSectorsPaginatedDocument,
} from "api/generated";
import { getRequestInstance } from "api/services/utils";
import { RequestTypes } from "api/services/enums";
import { PartialNullable } from "config";

import {
  IVentureSectorCreateVars,
  IVentureSectorListVars,
  IVentureSectorModel,
  IVentureSectorUpdateVars,
  IVentureSectorsPaginatedResponse,
  IVentureSectorsPaginatedVars,
  VentureSectorListResponse,
} from "./types";
import { client } from "config/apollo";
import { executeEviction } from "api/utils/cache-eviction";

export async function getVentureSectorList(
  variables: IVentureSectorListVars
): Promise<VentureSectorListResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureSectorListDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.ventureSectorList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getVentureSectorsPaginated(
  variables: IVentureSectorsPaginatedVars
): Promise<IVentureSectorsPaginatedResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureSectorsPaginatedDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data?.ventureSectorsPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createVentureSector(
  variables: IVentureSectorCreateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureSectorCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureSector",
      fieldNames: ["ventureSectorsPaginated", "ventureSectorList"],
    });

    return res?.data?.ventureSectorCreate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function updateVentureSector(
  variables: IVentureSectorUpdateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureSectorUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureSector",
      id: variables.ventureSectorId.toString(),
      fieldNames: [
        "ventureSectorsPaginated",
        "ventureSectorGetById",
        "ventureSectorList",
      ],
    });

    return res?.data?.ventureSectorUpdate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function getVentureSector(
  ventureSectorId: number
): Promise<PartialNullable<IVentureSectorModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureSectorGetByIdDocument,
      variables: {
        ventureSectorId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.ventureSectorGetById;
  } catch (error) {
    throw error;
  }
}

export async function deleteVentureSector(
  ventureSectorId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureSectorDeleteDocument,
      variables: {
        ventureSectorId,
      },
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureSector",
      id: ventureSectorId.toString(),
      fieldNames: ["ventureSectorsPaginated", "ventureSectorList"],
    });

    return Boolean(res?.data?.ventureSectorDelete);
  } catch (error) {
    throw error;
  }
}
